import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { UserFormModal } from "../components/users/UserFormModal";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { PromptDialog } from "../components/ui/PromptDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt: Date | string | null;
  createdAt: Date | string;
  roles: Array<{ id: string; code: string; name: string }>;
}

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(dt);
}

export function UsersPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const [pwReset, setPwReset] = useState<UserRow | null>(null);
  const [hardDel, setHardDel] = useState<UserRow | null>(null);

  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!(meQ.data?.user as { isSuperAdmin?: boolean } | undefined)?.isSuperAdmin;
  const meId = (meQ.data?.user as { id?: string } | undefined)?.id ?? null;

  const input = useMemo(() => ({ page, pageSize, q: q.trim() || undefined }), [page, q]);
  const usersQ = trpc.user.list.useQuery(input);

  const toggleMut = trpc.user.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const resetPwMut = trpc.user.resetPassword.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const removeMut = trpc.user.remove.useMutation({ onSuccess: () => { qc.invalidateQueries(); setHardDel(null); } });

  const rows = (usersQ.data?.rows ?? []) as unknown as UserRow[];
  const total = usersQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(row: UserRow) {
    setEditing(row);
    setModalOpen(true);
  }
  function handleToggle(row: UserRow) {
    const newState = !row.isActive;
    setConfirm({
      title: newState ? "Kullanıcıyı aktifleştir?" : "Kullanıcıyı devre dışı bırak?",
      message: newState ? (
        <><b>{row.email}</b> kullanıcısını tekrar aktifleştir?</>
      ) : (
        <><b>{row.email}</b> kullanıcısını devre dışı bırak? Oturum açamayacak.</>
      ),
      tone: newState ? "primary" : "danger",
      confirmLabel: newState ? "Aktifleştir" : "Devre Dışı Bırak",
      run: () => toggleMut.mutate({ id: row.id, isActive: newState }),
    });
  }
  function handleReset(row: UserRow) {
    resetPwMut.reset();
    setPwReset(row);
  }

  const dialogs = (
    <>
      <UserFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />

      <PromptDialog
        open={!!pwReset}
        title="Parolayı sıfırla"
        message={
          <>
            <b>{pwReset?.email}</b> için yeni bir parola belirleyin.
            {resetPwMut.error ? (
              <span role="alert" style={{ display: "block", marginTop: 6, color: "var(--danger)", fontSize: 12 }}>
                {resetPwMut.error.message}
              </span>
            ) : null}
          </>
        }
        label="Yeni parola"
        inputType="password"
        placeholder="En az 8 karakter"
        minLength={8}
        icon="settings"
        confirmLabel="Parolayı Güncelle"
        busy={resetPwMut.isPending}
        trim={false}
        onConfirm={(pw) => resetPwMut.mutate({ id: pwReset!.id, newPassword: pw }, { onSuccess: () => setPwReset(null) })}
        onCancel={() => { resetPwMut.reset(); setPwReset(null); }}
      />

      <HardDeleteConfirm
        open={!!hardDel}
        code={hardDel?.email ?? ""}
        title="Kullanıcıyı kalıcı sil"
        busy={removeMut.isPending}
        errorMessage={removeMut.error?.message ?? null}
        warning={
          <>
            <b>{hardDel?.firstName} {hardDel?.lastName}</b> (<b>{hardDel?.email}</b>) kullanıcısını kalıcı olarak silmek üzeresiniz.
            Rol ve üretim merkezi atamaları silinir; geçmiş kayıtlarda (denetim, arıza, bakım, satınalma) adı anonimleşir.
            Bu işlem <b>geri alınamaz</b>.
          </>
        }
        onConfirmed={() => hardDel && removeMut.mutate({ id: hardDel.id })}
        onClose={() => { removeMut.reset(); setHardDel(null); }}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={openCreate}><Icon name="plus" size={15} />Yeni Kullanıcı</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="E-posta, ad ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
        </div>

        {usersQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : usersQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{usersQ.error?.message}</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q ? `"${q}" için sonuç yok.` : "Henüz kullanıcı yok"}</div>
        ) : (
          <>
            {rows.map((u) => (
              <MCard pad key={u.id}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{u.firstName} {u.lastName}</span>
                    <span style={{ display: "block", fontSize: 11.5, color: "var(--neutral-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span>
                  </span>
                  {u.isActive ? <span className="badge b-success no-dot">Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
                  <span className="badge b-info no-dot">{(u as { tesisName?: string | null }).tesisName ?? "Global"}</span>
                  {u.roles.map((r) => <span key={r.id} className="badge b-neutral no-dot">{r.name}</span>)}
                </div>
                <div style={{ fontSize: 11, color: "var(--neutral-400)", marginTop: 8 }}>Son giriş: <span className="mono">{formatDate(u.lastLoginAt)}</span></div>
                <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => openEdit(u)}><Icon name="edit" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Şifre sıfırla" onClick={() => handleReset(u)} disabled={resetPwMut.isPending}><Icon name="settings" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" style={{ color: u.isActive ? "var(--danger)" : "var(--success)" }} aria-label={u.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => handleToggle(u)} disabled={toggleMut.isPending}><Icon name={u.isActive ? "archive" : "check"} size={16} /></button>
                  {isSuperAdmin && u.id !== meId ? (
                    <button type="button" className="m-iconbtn m-li-act danger" aria-label="Kalıcı sil" onClick={() => { removeMut.reset(); setHardDel(u); }} disabled={removeMut.isPending}><Icon name="trash" size={16} /></button>
                  ) : null}
                </div>
              </MCard>
            ))}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        {dialogs}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Kullanıcılar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Kullanıcılar</h1>
          <p className="sub">Sistem kullanıcıları ve rol atamaları</p>
        </div>
        <button type="button" className="btn-md primary" onClick={openCreate}>
          <Icon name="plus" size={14} />Yeni Kullanıcı
        </button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="E-posta, ad ara…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              aria-label="Ara"
            />
          </div>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => usersQ.refetch()} disabled={usersQ.isFetching}>
            {usersQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        <div className="dt2-scroll">
          {usersQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : usersQ.isError ? (
            <div className="empty-state">
              <p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p>
              <p className="sub">{usersQ.error?.message}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Henüz kullanıcı yok</p>
              <p className="sub">{q ? `"${q}" için sonuç yok.` : "İlk kullanıcıyı ekleyin."}</p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th>E-posta</th>
                  <th>Ad Soyad</th>
                  <th style={{ width: 150 }}>Tesis</th>
                  <th>Roller</th>
                  <th style={{ width: 150 }}>Son Giriş</th>
                  <th style={{ width: 90 }}>Durum</th>
                  <th style={{ width: 110 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td><span className="cell-id" style={{ fontFamily: "var(--font-sans)" }}>{u.email}</span></td>
                    <td>{u.firstName} {u.lastName}</td>
                    <td style={{ fontSize: 12.5 }}>{(u as { tesisName?: string | null }).tesisName ?? <span style={{ color: "var(--neutral-400)" }}>Global</span>}</td>
                    <td>
                      <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                        {u.roles.map((r) => (
                          <span key={r.id} className="badge-pill">{r.name}</span>
                        ))}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{formatDate(u.lastLoginAt)}</td>
                    <td>
                      {u.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}
                    </td>
                    <td>
                      <div className="row-acts">
                        <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => openEdit(u)}>
                          <Icon name="edit" size={15} />
                        </button>
                        <button type="button" className="iact" aria-label="Şifre" title="Şifre" onClick={() => handleReset(u)} disabled={resetPwMut.isPending}>
                          <Icon name="settings" size={15} />
                        </button>
                        <button
                          type="button"
                          className={u.isActive ? "iact danger" : "iact success"}
                          aria-label={u.isActive ? "Pasifleştir" : "Aktifleştir"}
                          title={u.isActive ? "Pasifleştir" : "Aktifleştir"}
                          onClick={() => handleToggle(u)}
                          disabled={toggleMut.isPending}
                        >
                          <Icon name={u.isActive ? "archive" : "check"} size={15} />
                        </button>
                        {isSuperAdmin && u.id !== meId ? (
                          <button
                            type="button"
                            className="iact danger"
                            aria-label="Kalıcı sil"
                            title="Kalıcı sil (çift onaylı)"
                            onClick={() => { removeMut.reset(); setHardDel(u); }}
                            disabled={removeMut.isPending}
                          >
                            <Icon name="trash" size={15} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div>
            <b>{total}</b> kayıttan <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası
          </div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {dialogs}
    </div>
  );
}
